import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export const useCentralizedDeviceRealtime = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔴 [Realtime] Setting up centralized device_details subscription');
    }
    
    // Single channel for ALL device updates
    const channel = supabase
      .channel('all-devices-realtime')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'device_details',
        // NO FILTER - listen to all devices
      }, (payload) => {
        const deviceId = payload.new.device_id;
        
        if (import.meta.env.DEV) {
          console.log(`🔴 [Realtime] Device ${deviceId} updated via realtime push`);
        }
        
        // Update only this specific device in cache
        queryClient.setQueryData(
          ['device-details', deviceId],
          (oldData: any) => {
            if (!oldData) return payload.new;
            
            // Merge new data with existing, preserving structure
            return {
              ...oldData,
              ...payload.new,
              // Preserve camelCase fields from normalization
              deviceID: payload.new.device_id,
              deviceName: payload.new.device_name,
              deviceDescription: payload.new.device_description,
              deviceLastUpdated: payload.new.device_last_updated,
              deviceStatus: payload.new.device_status,
              deviceFirmware: payload.new.device_firmware,
              deviceSerial: payload.new.device_serial,
              currentHdop: payload.new.current_hdop,
              latWgs84: payload.new.lat_wgs84,
              longWgs84: payload.new.long_wgs84,
              groupName: payload.new.group_name,
              groupReference: payload.new.group_reference,
              contract: {
                imagesAllowed: payload.new.images_allowed,
                imagesCurrent: payload.new.images_current,
                imagesOverflow: payload.new.images_overflow,
                scheduleStart: payload.new.notification?.scheduleStart,
                scheduleEnd: payload.new.notification?.scheduleEnd,
                interval: payload.new.notification?.interval,
                enablePublic: payload.new.notification?.enablePublic,
                onDemand: payload.new.notification?.onDemand,
              },
              notification: payload.new.notification,
              imageKit: payload.new.imagekit,
              cellular: payload.new.cellular,
              battery: payload.new.battery,
              gnss: payload.new.gnss,
              location: payload.new.location,
              safeSearch: payload.new.safe_search,
              camera_config: payload.new.camera_config,
              notificationAiModel: payload.new.notification_ai_model,
              pastImages: payload.new.past_images,
              notification_data_tags: payload.new.notification_data_tags,
              notification_data: payload.new.notification_data,
            };
          }
        );
      })
      .subscribe((status) => {
        if (import.meta.env.DEV) {
          console.log('🔴 [Realtime] Subscription status:', status);
        }
        
        if (status === 'SUBSCRIBED') {
          if (import.meta.env.DEV) {
            console.log('✅ [Realtime] Successfully subscribed to all device updates');
          }
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.error('❌ [Realtime] Connection lost, will auto-retry');
        }
      });

    return () => {
      if (import.meta.env.DEV) {
        console.log('🔴 [Realtime] Cleaning up centralized subscription');
      }
      channel.unsubscribe();
      supabase.removeChannel(channel);
    };
  }, [queryClient]);
};
